
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import { deleteObjectFromArray } from "@/helpers";
import { updateObject } from "@/interfaces/Additional.interfaces";
import { updateTable } from "@/api";
import { isLifecycleOwnedTalentPaymentMutation } from "@/helpers/paymentLifecycleAdminGuard";

export default Vue.extend({
  name: "ConfirmDialog",
  data: () => {
    return {
      headers: [
        { text: "Table name", value: "tablename" },
        { text: "ID", value: "columnId" },
        { text: "Column", value: "column" },
        { text: "Previous value", value: "prevValue" },
        { text: "Changed value", value: "value" },
        { text: "Cancel", value: "cancel" }
      ],
      loading: false,
      saveError: ""
    };
  },
  computed: {
    ...mapState([
      "preparedDataToSave",
      "confirmDialogShow",
      "tableContent",
      "tablename"
    ])
  },
  methods: {
    ...mapMutations(["modify", "modifyMultiple", "callSnackbar"]),
    dismiss(visible: boolean) {
      if (!visible && !this.loading) {
        this.modify({ property: "confirmDialogShow", value: false });
      }
    },
    close() {
      if (this.loading) return;
      this.saveError = "";
      const changes = [...this.preparedDataToSave];
      changes.forEach((change: updateObject) => this.remove(change));
      this.modify({ property: "confirmDialogShow", value: false });
      if (changes.some(change => change.tablename.toLowerCase() === "shift")) {
        this.$root.$emit("admin-shift-hours-refresh");
      }
    },
    async save() {
      if (this.loading) return;
      this.loading = true;
      this.saveError = "";

      try {
        if (
          this.preparedDataToSave.some(isLifecycleOwnedTalentPaymentMutation)
        ) {
          this.callSnackbar({
            visible: true,
            text:
              "Talent payment status requires the audited confirmation action",
            color: "red darken-2"
          });
          return;
        }
        const changes: updateObject[] = [...this.preparedDataToSave];
        if (!changes.length) return;
        const hours = changes.filter(
          change =>
            change.tablename.toLowerCase() === "shift" &&
            ["totalhours", "unpaidhours", "billablehours"].includes(
              change.column.toLowerCase()
            )
        );
        if (
          new Set(hours.map(change => String(change.columnId))).size !==
          hours.length
        ) {
          throw new Error(
            "Save one hours field per shift at a time. The other hours update automatically."
          );
        }
        const results = await Promise.allSettled(
          changes.map((obj: updateObject) => {
            return updateTable({
              tablename: obj.tablename,
              columnname: obj.column,
              id: obj.columnId,
              prevValue: obj.prevValue,
              value: obj.value
            });
          })
        );

        const failed = changes.filter(
          (_, index) => results[index].status === "rejected"
        );
        this.modifyMultiple([
          { property: "preparedDataToSave", value: failed },
          { property: "confirmDialogShow", value: failed.length > 0 }
        ]);

        if (
          results.some(
            result =>
              result.status === "fulfilled" &&
              result.value &&
              (result.value as { invoiceHoursSynchronized?: boolean })
                .invoiceHoursSynchronized === true
          )
        ) {
          this.$root.$emit("admin-shift-hours-refresh");
        }

        const refusal = results.find(result => result.status === "rejected") as
          | PromiseRejectedResult
          | undefined;
        if (refusal) {
          const message =
            refusal.reason instanceof Error
              ? refusal.reason.message
              : "Data not saved";
          this.saveError =
            failed.length < changes.length
              ? `Some changes were saved. ${message}`
              : message;
          this.callSnackbar({
            visible: true,
            text: this.saveError,
            color: "red darken-2"
          });
          return;
        }

        this.callSnackbar({
          visible: true,
          text: "Data saved",
          color: "green darken-2"
        });
      } catch (error) {
        this.saveError =
          error instanceof Error ? error.message : "Data not saved";
        this.callSnackbar({
          visible: true,
          text: this.saveError,
          color: "red darken-2"
        });
      } finally {
        this.loading = false;
      }
    },
    remove(obj: updateObject) {
      if (this.loading) return;
      this.saveError = "";
      const data = deleteObjectFromArray(this.preparedDataToSave, obj);

      const tableContent = this.tableContent.map((i: any) => {
        if (
          String(this.tablename).toLowerCase() ===
            obj.tablename.toLowerCase() &&
          i.id === obj.columnId
        ) {
          i[obj.column] = obj.prevValue;
        }
        return i;
      });

      let actions = [
        { property: "tableContent", value: tableContent },
        { property: "preparedDataToSave", value: data }
      ];

      if (data && data.length === 0) {
        actions.push({ property: "confirmDialogShow", value: false });
      }

      this.modifyMultiple(actions);
    }
  }
});
